import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { blogService, BlogPost, normalizeImagePath } from '../../services/blogService';
import { PAGES } from '../../seo/documentHead';

const StoriesPage: React.FC = () => {
  const [stories, setStories] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });

  const fetchStories = async (page = 1) => {
    try {
      setLoading(true);
      setError(null);
      const response = await blogService.fetchPublishedStories(page, 10);
      setStories(response.data);
      setPagination({
        page: response.pagination.page,
        pages: response.pagination.pages,
        total: response.pagination.total
      });
    } catch (err) {
      console.error('Error fetching stories:', err);
      setError(err instanceof Error ? err.message : 'Failed to load stories');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStories();
  }, []);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // The page header stays up while loading and on error, so the prerendered
  // header (scripts/prerender-fallback.js) doesn't blink out and back in.
  const pageHeader = (
    <div className="text-center mb-5">
      <h1 className="display-4 fw-bold">{PAGES['/stories'].h1}</h1>
      <p className="lead text-muted">{PAGES['/stories'].intro}</p>
    </div>
  );

  if (loading) {
    return (
      <div className="container py-5">
        {pageHeader}
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container py-5">
        {pageHeader}
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="container py-5">
      {pageHeader}

      {stories.length === 0 ? (
        <div className="text-center py-5">
          <p className="text-muted">No stories available yet. Check back soon!</p>
        </div>
      ) : (
        <>
          <div className="row g-4">
            {stories.map((story) => (
              <div key={story._id} className="col-md-6 col-lg-4">
                <div className="card h-100 shadow-sm">
                  {story.featuredImage && (
                    <img
                      src={normalizeImagePath(story.featuredImage.path)}
                      alt={story.featuredImage.alt || story.title}
                      className="card-img-top"
                      style={{ height: '200px', objectFit: 'cover' }}
                    />
                  )}
                  <div className="card-body d-flex flex-column">
                    <h5 className="card-title">{story.title}</h5>
                    <p className="card-text text-muted small mb-2">
                      {formatDate(story.publishedAt || story.createdAt)}
                    </p>
                    <p className="card-text flex-grow-1">{story.excerpt}</p>
                    <Link to={`/stories/${story.slug}`} className="btn btn-outline-primary mt-auto">
                      Read More
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <nav aria-label="Stories pagination" className="mt-5">
              <ul className="pagination justify-content-center">
                <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => fetchStories(pagination.page - 1)}
                    disabled={pagination.page === 1}
                  >
                    Previous
                  </button>
                </li>
                {Array.from({ length: pagination.pages }, (_, i) => i + 1).map((pageNum) => (
                  <li key={pageNum} className={`page-item ${pagination.page === pageNum ? 'active' : ''}`}>
                    <button className="page-link" onClick={() => fetchStories(pageNum)}>
                      {pageNum}
                    </button>
                  </li>
                ))}
                <li className={`page-item ${pagination.page === pagination.pages ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => fetchStories(pagination.page + 1)}
                    disabled={pagination.page === pagination.pages}
                  >
                    Next
                  </button>
                </li>
              </ul>
            </nav>
          )}
        </>
      )}

      {/* CTA Section */}
      <div className="text-center mt-5 py-4 bg-light rounded">
        <h4>Inspired by these stories?</h4>
        <p className="text-muted">Share your own words of encouragement with the world.</p>
        <Link to="/submit" className="btn btn-primary btn-lg">
          <i className="fas fa-edit me-2"></i>Write a Note
        </Link>
      </div>
    </div>
  );
};

export default StoriesPage;
